import { ReactNode } from 'react';
import { FilterCategory, FilterPrimitive } from '../types/filters';
import getFormattedLabel from './get-formatted-label';
import { cn } from '@/shadcn-ui/utils';
import mapFuelTypeLabel from './map-fuel-type-label';

interface GetFormattedLabelProps {
  /** The category of the filter. */
  categoryName: FilterCategory;
  /** The actual filter value which needs to be formatted. */
  filter: FilterPrimitive;
  count?: number;
  disabled?: boolean;
}

/**
 * Converts a filter value to a label based on its category and type.
 *
 * @param {FilterCategory} categoryName - The category to which the label belongs.
 * @param {FilterPrimitive} label - The filter value which needs to be formatted.
 * @returns {string} - The formatted label.
 */
export const getFormattedFilterLabel = ({
  categoryName,
  filter,
  count,
  disabled,
}: GetFormattedLabelProps): string | ReactNode => {
  if (categoryName === 'shipping' && filter === 'local_only') {
    return (
      <>
        <span
          className={cn('block lg:hidden', {
            'cursor-not-allowed text-flexcar-neutral80': disabled,
          })}
        >
          Local cars only - <i>No shipping fee</i>{' '}
          {count !== undefined ? (
            <span
              className={cn({
                'text-flexcar-neutral60': !disabled,
                'text-flexcar-neutral80': disabled,
              })}
            >
              ({count})
            </span>
          ) : null}
        </span>
        <span
          className={cn('hidden lg:block', {
            'cursor-not-allowed text-flexcar-neutral80': disabled,
          })}
        >
          Local cars only <br />
          <i>No shipping fee</i>
          <br />
          {count !== undefined ? (
            <span
              className={cn({
                'text-flexcar-neutral60': !disabled,
                'text-flexcar-neutral80': disabled,
              })}
            >
              ({count})
            </span>
          ) : null}
        </span>
      </>
    );
  }

  // Note: We assume that all strings are valid filter names
  if (typeof filter === 'string') {
    // TODO: remove these once fuel type data is fixed
    const stringFilter = categoryName === 'fuel_type' ? mapFuelTypeLabel(filter) : filter;
    return getFormattedLabel(stringFilter, count, disabled);
  }

  if (typeof filter === 'number')
    return getFormattedLabel(filter.toString(), count, disabled);

  return 'Invalid filter';
};
